// import { toNumber } from '@freelancer/datastore/core';
import { Country } from '@freelancer/datastore/countries';
import { _transformCurrency } from '@freelancer/datastore/currencies';
import { Qualification } from '@freelancer/datastore/project-view-projects';
import { _transformUserImage } from '@freelancer/datastore/users';
import {
  CountryApi,
  LocationApi,
  QualificationApi,
} from 'api-typings/common/common';
import {
  BadgeApi,
  MembershipPackageApi,
  ReputationApi,
  ReputationDataApi,
  UserApi,
  UserStatusApi,
} from 'api-typings/users/users';
import { Badge } from './badge.model';
import { Location } from './location.model';
import {
  MembershipPackage,
  ProjectViewUser,
  UserResponsiveness,
} from './project-view-users.model';
import { Reputation, ReputationData } from './reputation.model';
import { UserStatus } from './user-status.model';

// FIXME: optionals are a mess, 'private' _transformer imports which we use
// here to reuse logic, work out appropriate default values, eliminate throws
// as much as possible
export function transformProjectViewUsers(user: UserApi): ProjectViewUser {
  if (
    !user.id ||
    !user.username ||
    !user.avatar_cdn ||
    !user.avatar_large_cdn ||
    !user.role ||
    !user.chosen_role ||
    !user.registration_date ||
    !user.status ||
    !user.primary_currency
  ) {
    throw new ReferenceError(`Missing a required user field.`);
  }

  return {
    id: user.id,
    username: user.username,
    displayName: user.public_name || user.display_name || user.username,
    avatar: _transformUserImage(user.avatar_cdn),
    avatarLarge: _transformUserImage(user.avatar_large_cdn),
    role: user.role,
    chosenRole: user.chosen_role,
    profileUrl: `/u/${user.username}`,
    badges: (user.badges || []).map(transformBadge),
    qualifications: (user.qualifications || []).map(transformQualification),
    status: transformUserStatus(user.status),
    hourlyRate: user.hourly_rate,
    tagLine: user.tagline || '',
    reputation: user.reputation
      ? transformReputation(user.reputation)
      : undefined,
    employerReputation: user.employer_reputation
      ? transformReputation(user.employer_reputation)
      : undefined,
    primaryCurrency: _transformCurrency(user.primary_currency),
    primaryLanguage: user.primary_language,
    responsiveness: transformResponsiveness(user.responsiveness),
    registrationDate: user.registration_date * 1000,
    location: user.location ? transformLocation(user.location) : undefined,
    membershipPackage: user.membership_package
      ? transformMembership(user.membership_package)
      : undefined,
    isNewFreelancer: user.reputation
      ? transformIsNewFreelancer(user.reputation)
      : undefined,
  };
}

export function transformUserStatus(status: UserStatusApi): UserStatus {
  return {
    paymentVerified: status.payment_verified || false,
    emailVerified: status.email_verified || false,
    depositMade: status.deposit_made || false,
    profileComplete: status.profile_complete || false,
    phoneVerified: status.phone_verified || false,
    identityVerified: status.identity_verified || false,
    facebookConnected: status.facebook_connected || false,
  };
}

export function transformBadge(badge: BadgeApi): Badge {
  if (
    !badge.id ||
    !badge.name ||
    !badge.description ||
    !badge.time_awarded ||
    !badge.icon_url
  ) {
    throw new ReferenceError(`Missing a required timezone field.`);
  }

  return {
    id: badge.id,
    name: badge.name,
    description: badge.description,
    timeAwarded: badge.time_awarded,
    iconUrl: badge.icon_url,
  };
}

export function transformQualification(
  qualification: QualificationApi,
): Qualification {
  if (!qualification.icon_url) {
    throw new ReferenceError(`Missing an icon_url field.`);
  }

  return {
    id: qualification.id,
    name: qualification.name,
    level: qualification.level,
    type: qualification.type,
    iconUrl: qualification.icon_url.replace('/img/insignia/', ''),
    description: qualification.description,
    iconName: qualification.icon_name,
    scorePercentage: qualification.score_percentage,
    userPercentile: qualification.user_percentile,
  };
}

// NOTE: Readd this and update the model if timezone is needed
// export function transformTimezone(timezone: TimezoneApi): Timezone {
//   if (
//     !timezone.id ||
//     !timezone.country ||
//     !timezone.timezone ||
//     !timezone.offset
//   ) {
//     throw new ReferenceError(`Missing a required timezone field.`);
//   }

//   return {
//     id: timezone.id,
//     country: timezone.country,
//     timezone: timezone.timezone,
//     offset: timezone.offset,
//   };
// }

export function transformLocation(location: LocationApi): Location {
  if (!location.country) {
    throw new ReferenceError(`Missing a required location field.`);
  }

  return {
    country: transformCountry(location.country),
    city: location.city,
    mapCoordinates:
      location.latitude !== undefined && location.longitude !== undefined
        ? {
            latitude: location.latitude,
            longitude: location.longitude,
          }
        : undefined,
    vicinity: location.vicinity,
    administrativeArea: location.administrative_area,
    fullAddress: location.full_address,
  };
}

// the countries returned from the Users API can be a little wonky sometimes
// this function is a bit more "safe" than the one in countries.transformers
function transformCountry(country: CountryApi): Country {
  return {
    id: country.code || '',
    code: country.code || '',
    name: country.name,
    flagUrl: country.flag_url,
  };
}

export function transformReputation(reputation: ReputationApi): Reputation {
  return {
    entireHistory: transformReputationData(reputation.entire_history),
    last3Months: transformReputationData(reputation.last3months),
    last12Months: transformReputationData(reputation.last12months),
    userId: reputation.user_id,
    role: reputation.role,
    earningsScore: reputation.earnings_score,
    jobHistory: reputation.job_history,
    projectStats: reputation.project_stats,
  };
}

export function transformReputationData(
  repData: ReputationDataApi,
): ReputationData {
  return {
    overall: repData.overall,
    onBudget: repData.on_budget,
    onTime: repData.on_time,
    positive: repData.positive,
    all: repData.all,
    reviews: repData.reviews,
    incompleteReviews: repData.incomplete_reviews,
    complete: repData.complete,
    incomplete: repData.incomplete,
    earnings: repData.earnings,
    completionRate: Math.round(repData.completion_rate * 10 ** 4) / 10 ** 2,
    rehireRate: repData.rehire_rate,
    categoryRatings: repData.category_ratings,
  };
}

function transformMembership(
  membership: MembershipPackageApi,
): MembershipPackage {
  if (!membership.name) {
    throw Error(`Membership Package Object does not have name attribute.`);
  }
  if (!membership.id) {
    throw Error(`Membership Package Object does not have id attribute.`);
  }

  return {
    id: membership.id,
    name: membership.name,
  };
}

function transformIsNewFreelancer(reputation: ReputationApi): boolean {
  return (
    !reputation.entire_history.reviews &&
    !reputation.earnings_score &&
    !reputation.entire_history.all
  );
}

function transformResponsiveness(
  responsiveness: number | undefined,
): UserResponsiveness {
  switch (responsiveness) {
    case 1:
    case 2:
    case 3:
    case 4:
    case 5:
      return responsiveness;
    default:
      return undefined;
  }
}
